import { Link } from "react-router-dom";
import { Page, Reveal, MaskedLine, Eyebrow, Marquee } from "@/components/motion/Reveal";
import { IMG } from "@/constants/images";

const CHAPTERS = [
  { n: "01", title: "Inspiration", desc: "Every next chapter starts with seeing. We help you recognize what's possible, in yourself, your leadership, and your organization." },
  { n: "02", title: "Clarity", desc: "Inspiration without direction fades. We work with you to define exactly where you're going, and why it matters." },
  { n: "03", title: "Strategy", desc: "Vision deserves a plan. Together we build the strategy, systems, and structure required to move forward." },
  { n: "04", title: "Action", desc: "Momentum is built, not found. We turn intention into deliberate, accountable, forward action." },
];

const VALUES = [
  { title: "Purpose", desc: "Work should mean something. We begin with why." },
  { title: "Leadership", desc: "Everything rises and falls on the leader. We build them first." },
  { title: "Growth", desc: "Potential is not a destination; it is a discipline." },
  { title: "Impact", desc: "Success is measured by what outlasts you." },
];

const AUDIENCES = [
  "Executives & Professionals",
  "Entrepreneurs & Business Owners",
  "Women in Leadership",
  "Emerging & Young Professionals",
  "Organizations & Leadership Teams",
];

export default function About() {
  return (
    <Page data-testid="about-page">
      {/* HERO */}
      <section data-testid="about-hero" className="bg-ivory pt-40 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>About Genesis</Eyebrow>
          <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl text-obsidian mt-8 leading-[1.05]">
            <MaskedLine delay={0.2}>The Passion</MaskedLine>
            <MaskedLine delay={0.35} className="italic text-gold-dark">Behind Genesis</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="font-serif italic text-lg sm:text-xl text-stone-text mt-8 max-w-2xl">
              Helping people see what's possible, and helping them build it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section data-testid="about-story" className="bg-ivory pb-24 lg:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5 relative">
            <Reveal>
              <div className="absolute -inset-4 border border-gold/40 translate-x-4 translate-y-4 pointer-events-none" />
              <img
                src={IMG.founderAbout}
                alt="Sharon Tanyi, Founder & Senior Consultant"
                data-testid="about-founder-photo"
                className="relative w-full aspect-[4/5] object-cover object-top"
              />
            </Reveal>
            <Reveal delay={0.2}>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-text mt-6">
                Sharon Tanyi · Executive Coach • Entrepreneur • Motivational Speaker
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6">
            <Reveal>
              <p className="text-base sm:text-lg font-light leading-relaxed text-stone-text">
                Throughout a career spanning more than two decades, Sharon Tanyi has led
                teams, developed leaders, built businesses, and navigated organizational
                growth and change. She has experienced entrepreneurship firsthand (the risk, the resilience, the reward) and has coached professionals and
                leaders through their own defining chapters.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-stone-text">
                Her path has moved through multi-site
                operations, business ownership, executive leadership, consulting, and
                leadership development. Every chapter reinforced the same conviction:
                people are capable of far more than they often believe, and with
                clarity and strategy, they can build it.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border-l-2 border-gold pl-6 py-2 mt-4">
                <p className="font-serif italic text-xl text-obsidian leading-snug">
                  "I don't want to simply inspire you about what's possible. I want to
                  help you build it."
                </p>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-stone-text mt-4">
                  Sharon Tanyi
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY GENESIS */}
      <section data-testid="why-genesis-section" className="bg-obsidian text-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Why Genesis?</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-8 leading-tight">
                Genesis represents <span className="italic text-gold">a beginning.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.2}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-ivory/60">
                Not necessarily starting over, but recognizing that there may be more
                to build, more to become, and another chapter ahead. Genesis is the
                moment a new idea, a purpose, a vision, a business, a leadership
                journey, or a next chapter begins taking shape.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="font-serif italic text-xl text-gold mt-10">
                The Beginning of What's Next.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 mt-24">
          <Reveal><Eyebrow>The Genesis Approach</Eyebrow></Reveal>
          <div className="mt-12">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div data-testid={`manifesto-chapter-${c.n}`} className="grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-white/10 items-baseline">
                  <span className="font-mono text-xs text-gold md:col-span-1">{c.n}</span>
                  <h3 className="font-serif text-2xl lg:text-3xl md:col-span-4">{c.title}</h3>
                  <p className="text-sm sm:text-base font-light text-ivory/55 leading-relaxed md:col-span-7">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={["Purpose", "Leadership", "Growth", "Impact", "Build What's Next"]} dark={false} />

      {/* VALUES + WHO WE SERVE */}
      <section data-testid="values-section" className="bg-ivory py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>What We Stand For</Eyebrow></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-obsidian/10 border border-obsidian/10 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1} className="bg-ivory">
                <div className="p-10 h-full hover:bg-cream transition-colors duration-500" data-testid={`value-card-${v.title.toLowerCase()}`}>
                  <span className="font-serif text-4xl text-gold/50">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-obsidian mt-6">{v.title}</h3>
                  <p className="text-sm font-light text-stone-text mt-3 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-24 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <Reveal><Eyebrow>Who We Serve</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-serif text-3xl sm:text-4xl text-obsidian mt-8 leading-tight">
                  Built for those ready <span className="italic text-gold-dark">to move forward.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              {AUDIENCES.map((a, i) => (
                <Reveal key={a} delay={i * 0.07}>
                  <div className="flex items-baseline gap-6 py-5 border-b border-obsidian/10" data-testid={`audience-${i}`}>
                    <span className="font-mono text-xs text-gold">0{i + 1}</span>
                    <p className="font-serif text-lg sm:text-xl text-obsidian">{a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-24 text-center">
              <Link
                to="/contact"
                data-testid="about-cta-work-with-sharon"
                className="inline-block bg-obsidian text-ivory px-10 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
              >
                Work With Sharon
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
